import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { ClientRateconRevision, RateconRevisionOverrides, RateconRevisionRouteStop } from '@numeo/types-main-web-app'
import { Badge, Button, FieldMessage, Modal } from '@/components/ds2'
import { useAppDispatch } from '@/hooks'
import { updateSingleLoad } from 'store/loadsSlice'
import { getServerErrorMessage } from 'utils/get-user-error-message'
import { showWarning } from 'utils/snackbar'
import { AcceptedRateconRevision, rateconRevisionsApi } from '../api/ratecon-revisions.api'
import { DiffRow, isAlreadyResolvedError, RateconRevisionDiffTable, RateconRevisionDocumentNotice, RateconRevisionKindBadges } from './RateconRevisionDiff'
import { RateconRevisionLoadSearchOption, RateconRevisionLoadSelector } from './RateconRevisionLoadSelector'
import { RateconRevisionRouteComparison, RateconRevisionRouteShapeNotice } from './RateconRevisionRouteComparison'

/** Long enough to copy the appointments out of: it names what the dispatcher now has to enter by hand. */
const UNAPPLIED_WARNING_MS = 15000

interface RateconRevisionModalProps {
    revision: ClientRateconRevision
    onClose: () => void
    onResolved: (loadId: string) => void
    /** Place in the review queue, e.g. `{ current: 2, total: 5 }`. Omitted when reviewing a single
     *  revision outside the banner's queue. */
    position?: { current: number; total: number }
    /** Step through the queue without acting. Omitted at the respective end of the queue. */
    onPrev?: () => void
    onNext?: () => void
    loadSearchOptions?: RateconRevisionLoadSearchOption[]
    onLoadSearchSelect?: (revisionId: string) => void
}

const ROUTE_FIELD_KEY_REGEX = /^route\[(\d+)\]\.(date|time|arrivalDate|deadlineDate)$/
const ROUTE_LOCATION_KEY_REGEX = /^route\[(\d+)\]\.location$/

const formatCurrency = (amount: number) => `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0 })}`

const ROUTE_FIELD_LABELS: Record<'date' | 'time' | 'arrivalDate' | 'deadlineDate', string> = {
    date: 'date',
    time: 'time',
    arrivalDate: 'arrival date',
    deadlineDate: 'deadline',
}

/** Facility first, then the postal address — the same order the route comparison uses, so a stop
 *  reads the same wherever it appears in this modal. */
const formatStopAddress = (stop?: RateconRevisionRouteStop): string => {
    if (!stop) return '—'
    const place = [stop.city, stop.state].filter(Boolean).join(', ')
    return [stop.facility, stop.street, place, stop.zip].filter(Boolean).join(' · ') || '—'
}

/** arrivalDate/deadlineDate arrive over the wire as ISO strings (JSON has no Date type) despite the shared type saying `Date | null` — format defensively. */
export const formatRouteFieldValue = (value: string | Date | null | undefined): string => {
    if (!value) return '—'
    if (typeof value === 'string') return value
    return new Date(value).toLocaleString()
}

/** Narrowed to just the fields the diff needs — lets the Timeline reuse this against raw log
 *  metadata (v1/v2/changedFields) without needing a full ClientRateconRevision (id, status, etc.). */
export const buildDiffRows = (revision: Pick<ClientRateconRevision, 'v1' | 'v2' | 'changedFields'>): DiffRow[] => {
    const rows: DiffRow[] = []

    if (revision.changedFields.includes('rate')) {
        const delta = revision.v2.rate - revision.v1.rate
        rows.push({
            key: 'rate',
            label: 'Rate',
            original: formatCurrency(revision.v1.rate),
            revised: formatCurrency(revision.v2.rate),
            delta: delta === 0 ? '—' : `${delta > 0 ? '+' : '-'}${formatCurrency(Math.abs(delta))}`,
            deltaTone: delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'neutral',
            editable: true,
        })
    }

    revision.changedFields.forEach((field) => {
        const locationMatch = field.match(ROUTE_LOCATION_KEY_REGEX)
        if (!locationMatch) return
        const stopIndex = Number(locationMatch[1])
        const stopLabel = revision.v1.route[stopIndex]?.type === 'pickup' ? 'Pickup' : 'Delivery'
        rows.push({
            key: field,
            label: `${stopLabel} address`,
            original: formatStopAddress(revision.v1.route[stopIndex]),
            revised: formatStopAddress(revision.v2.route[stopIndex]),
            delta: 'relocated',
            deltaTone: 'neutral',
            editable: false,
        })
    })

    revision.changedFields.forEach((field) => {
        const match = field.match(ROUTE_FIELD_KEY_REGEX)
        if (!match) return
        const stopIndex = Number(match[1])
        const routeField = match[2] as 'date' | 'time' | 'arrivalDate' | 'deadlineDate'
        const stopLabel = revision.v1.route[stopIndex]?.type === 'pickup' ? 'Pickup' : 'Delivery'
        rows.push({
            key: field,
            label: `${stopLabel} ${ROUTE_FIELD_LABELS[routeField]}`,
            original: formatRouteFieldValue(revision.v1.route[stopIndex]?.[routeField]),
            revised: formatRouteFieldValue(revision.v2.route[stopIndex]?.[routeField]),
            delta: 'moved',
            deltaTone: 'neutral',
            editable: true,
        })
    })

    return rows
}

/** The editable (unformatted) form of a changed field's v2 value — date/time are already plain
 *  strings on the wire; arrivalDate/deadlineDate go through ISO so the value re-parses reliably
 *  regardless of the viewer's locale (formatRouteFieldValue's toLocaleString() would not). */
const getEditableValue = (revision: Pick<ClientRateconRevision, 'v1' | 'v2'>, key: string): string => {
    if (key === 'rate') return String(revision.v2.rate)
    const match = key.match(ROUTE_FIELD_KEY_REGEX)
    if (!match) return ''
    const stopIndex = Number(match[1])
    const routeField = match[2] as 'date' | 'time' | 'arrivalDate' | 'deadlineDate'
    const value = revision.v2.route[stopIndex]?.[routeField]
    if (!value) return ''
    return typeof value === 'string' ? value : new Date(value).toISOString()
}

/** Small circular numbered badge that overlaps the top-left corner of a section, matching the
 *  mockup's step-numbered diff/state/action sections (see numeo-mockups OngoingLoads.tsx DiffModal). */
const StepBadge = ({ n, tone = 'accent' }: { n: number; tone?: 'accent' | 'warning' }) => (
    <span className="absolute -left-2.5 -top-2.5">
        <Badge variant="primary" type={tone} size="sm" label={String(n)} />
    </span>
)

export const RateconRevisionModal = ({
    revision,
    onClose,
    onResolved,
    position,
    onPrev,
    onNext,
    loadSearchOptions = [],
    onLoadSearchSelect,
}: RateconRevisionModalProps) => {
    const dispatch = useAppDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isEditing, setIsEditing] = useState(false)
    const [editValues, setEditValues] = useState<Record<string, string>>({})

    // Stepping to another revision in the queue keeps this component mounted (remounting would
    // replay the entrance animation and make the dialog jump), so per-revision state has to be
    // cleared here or an edit-merge draft would carry onto the next load. Done during render
    // rather than in an effect so the incoming revision never paints with the previous one's
    // draft values first — https://react.dev/learn/you-might-not-need-an-effect
    const [renderedRevisionId, setRenderedRevisionId] = useState(revision.id)
    if (renderedRevisionId !== revision.id) {
        setRenderedRevisionId(revision.id)
        setIsEditing(false)
        setEditValues({})
        setError(null)
    }

    const diffRows = buildDiffRows(revision)

    const valueFor = (row: DiffRow): string => editValues[row.key] ?? getEditableValue(revision, row.key)

    const startEditMerge = () => {
        setEditValues(Object.fromEntries(diffRows.filter((row) => row.editable).map((row) => [row.key, valueFor(row)])))
        setIsEditing(true)
    }

    const cancelEditMerge = () => {
        setIsEditing(false)
        setEditValues({})
    }

    const buildOverrides = (): RateconRevisionOverrides => {
        const overrides: RateconRevisionOverrides = {}
        diffRows.forEach((row) => {
            // An address row carries no input, so `valueFor` would read its formatted display text
            // and submit that as an override of a field the backend does not accept.
            if (!row.editable) return
            const value = valueFor(row)
            if (row.key === 'rate') {
                // Number('') is 0 and isNaN(0) is false, so a cleared field used to submit an
                // override of 0 — which the backend reads as a deliberate rate and writes to
                // totalRate/originalRate, invoicing and factoring the load at nothing. An empty or
                // non-positive rate is not an edit; leave the field out and let v2 stand.
                const parsed = Number(String(value).trim())
                if (String(value).trim() !== '' && !isNaN(parsed) && parsed > 0) overrides.rate = parsed
                return
            }
            const match = row.key.match(ROUTE_FIELD_KEY_REGEX)
            if (!match) return
            const stopIndex = Number(match[1])
            const routeField = match[2] as 'date' | 'time' | 'arrivalDate' | 'deadlineDate'
            // An emptied appointment field is not an edit either. The backend takes date/time
            // overrides verbatim, so submitting '' would leave a booked stop with no appointment
            // time — which the ETA engine, the driver templates and the Timeline all render blank.
            if (String(value).trim() === '') return
            overrides.route = { ...overrides.route, [stopIndex]: { ...overrides.route?.[stopIndex], [routeField]: value } }
        })
        return overrides
    }

    const resolveWith = async (action: () => Promise<void>, failureMessage: string) => {
        setIsSubmitting(true)
        setError(null)
        try {
            await action()
        } catch (err) {
            if (isAlreadyResolvedError(err)) {
                // Nothing left to accept/reject — close out to the now-current list instead of
                // leaving a dead modal open with a "try again" that would fail identically.
                onResolved(revision.loadId)
                return
            }
            setError(getServerErrorMessage(err) ?? failureMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    const finishAccept = ({ load, warning }: AcceptedRateconRevision) => {
        // The accept endpoint already returns the post-update load — push it straight into the
        // loads slice (the pattern used across this feature, e.g. DriverIdleTimeWarning,
        // useStatusChange, useRefreshETA) so the Ongoing Loads table reflects the new rate/dates
        // immediately instead of showing stale data until a manual page reload.
        dispatch(updateSingleLoad(load))
        // Accepted, with appointments the load's current stops could not take. Said as a toast rather
        // than kept in the modal: the modal closes, and the dispatcher must still see it.
        if (warning) showWarning(warning, UNAPPLIED_WARNING_MS)
        onResolved(revision.loadId)
    }

    const handleAccept = () =>
        resolveWith(async () => {
            finishAccept(await rateconRevisionsApi.accept(revision.id))
        }, 'Failed to accept revision. Please try again.')

    const handleSaveAndAccept = () =>
        resolveWith(async () => {
            finishAccept(await rateconRevisionsApi.accept(revision.id, buildOverrides()))
        }, 'Failed to save and accept. Please try again.')

    const handleKeepOriginal = () =>
        resolveWith(async () => {
            await rateconRevisionsApi.reject(revision.id)
            onResolved(revision.loadId)
        }, 'Failed to keep original. Please try again.')

    return (
        <Modal
            open
            onClose={() => !isSubmitting && onClose()}
            size="lg"
            data-testid="ratecon-revision-modal"
            header={
                <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-base font-semibold text-foreground">Review revision · Load {revision.rateConfDocumentId}</h2>
                        <div className="flex-1" />
                        <RateconRevisionKindBadges revision={revision} />
                    </div>

                    {/* Queue stepper — only when several revisions are waiting, so a lone revision
                        keeps the simpler header. */}
                    {position && position.total > 1 && (
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-foreground-muted">
                                Revision {position.current} of {position.total}
                            </span>
                            <div className="flex-1" />
                            <Button
                                variant="tertiary"
                                size="sm"
                                onClick={onPrev}
                                disabled={!onPrev || isSubmitting}
                                prefix={<ChevronLeft size={14} strokeWidth={1.5} />}
                            >
                                Previous
                            </Button>
                            <Button
                                variant="tertiary"
                                size="sm"
                                onClick={onNext}
                                disabled={!onNext || isSubmitting}
                                suffix={<ChevronRight size={14} strokeWidth={1.5} />}
                            >
                                Next
                            </Button>
                        </div>
                    )}

                    <RateconRevisionLoadSelector
                        currentRevisionId={revision.id}
                        options={loadSearchOptions}
                        totalRevisions={position?.total}
                        disabled={isSubmitting}
                        onSelect={onLoadSearchSelect}
                    />
                </div>
            }
        >
            {/* 1 — field-level diff */}
            <div className="relative rounded-2xl border-2 border-accent-soft p-3 pt-4">
                <StepBadge n={1} />
                <RateconRevisionDiffTable
                    revision={revision}
                    rows={diffRows}
                    isEditing={isEditing}
                    editValues={editValues}
                    onEditValue={(key, value) => setEditValues((prev) => ({ ...prev, [key]: value }))}
                />
            </div>

            {/* 1a — whose paperwork this actually is. Only ever set on a hand-filed document, and
                only when it names a different load: reviewing it then means deciding whether to move
                THIS load's rate and stops onto what ANOTHER load's confirmation says. The upload
                dialog says so once, to whoever uploaded it; this is what the reviewer sees. */}
            <RateconRevisionDocumentNotice revision={revision} />

            {/* 1b — the stops, whole, when they no longer line up. Only then: for an ordinary
                revision the field-level diff above already says everything, and a second table would
                just be noise. */}
            {revision.routeShapeChanged && (
                <div className="relative mt-4 rounded-2xl border-2 border-warning-soft p-3 pt-4">
                    <StepBadge n={2} tone="warning" />
                    <RateconRevisionRouteShapeNotice revision={revision} />
                    <div className="mt-3 max-h-[264px] overflow-y-auto">
                        <RateconRevisionRouteComparison v1Route={revision.v1.route} v2Route={revision.v2.route} />
                    </div>
                </div>
            )}

            {/* 2 — settlement-state context */}
            <div className="relative mt-4 rounded-2xl border-2 border-warning-soft p-3">
                <StepBadge n={revision.routeShapeChanged ? 3 : 2} tone="warning" />
                <div className="flex items-start gap-3 rounded-xl bg-warning-soft px-3 py-2.5 text-sm">
                    <Badge variant="soft" type="warning" size="md" label="STATE" style={{ flexShrink: 0 }} />
                    <span className="text-warning-soft-foreground">
                        This load has not been invoiced yet — accepting applies the change directly to the load. Keeping original leaves the load untouched and
                        dismisses this revision.
                    </span>
                </div>
            </div>

            {/* 3 — actions */}
            <div className="relative mt-4 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-accent-soft p-3">
                <StepBadge n={revision.routeShapeChanged ? 4 : 3} />
                {isEditing ? (
                    <>
                        <Button variant="primary" size="md" onClick={handleSaveAndAccept} disabled={isSubmitting}>
                            Save &amp; accept
                        </Button>
                        <Button variant="secondary" size="md" onClick={cancelEditMerge} disabled={isSubmitting}>
                            Cancel edit
                        </Button>
                        <div className="flex-1" />
                        <span className="ml-auto text-sm leading-5 text-foreground-muted">edit any merged value · unedited fields keep v2</span>
                    </>
                ) : (
                    <>
                        <Button variant="primary" size="md" onClick={handleAccept} disabled={isSubmitting}>
                            Accept revision
                        </Button>
                        <Button variant="secondary" size="md" onClick={handleKeepOriginal} disabled={isSubmitting}>
                            Keep original
                        </Button>
                        {diffRows.length > 0 && (
                            <Button variant="outline" size="md" onClick={startEditMerge} disabled={isSubmitting}>
                                Edit-merge…
                            </Button>
                        )}
                        <div className="flex-1" />
                        <span className="ml-auto text-sm leading-5 text-foreground-muted">
                            {position && position.current < position.total
                                ? `updates the load · logs Timeline · then goes to ${position.total - position.current} more`
                                : 'updates the load · logs Timeline'}
                        </span>
                    </>
                )}
            </div>

            {error && <FieldMessage variant="error">{error}</FieldMessage>}
        </Modal>
    )
}
